import {
  CatalogFilter,
  CatalogSchema,
  NavigationGroup,
  NavigationItem,
  StandaloneItem,
  UtilityItem,
  NavigationConfig,
} from "../types/navigation";
import { createDebugLogger } from "./debug";
import { getIconForEntity } from "./icons";
import { formatTitle, humanize } from "./text";
import { normalizeCatalogSchema } from "./catalogSchema";

const logger = createDebugLogger("NavigationDefaults");

/**
 * Default standalone navigation items (Docs, Settings, Admin)
 * These appear in the main navigation bar
 */
export const STANDALONE_DEFAULTS: Record<string, Partial<StandaloneItem>> = {
  docs: { title: 'Docs', to: '/docs', icon: 'book' },
  settings: { title: 'Settings', to: '/settings', icon: 'settings' },
  admin: { title: 'Admin', to: '/admin', icon: 'shield' },
};

/**
 * Default utility navigation items (Create, Search)
 * These appear on the right side of the navigation bar
 */
export const UTILITY_DEFAULTS: Record<string, Partial<UtilityItem>> = {
  create: { title: 'Create', to: '/create', icon: 'plus-circle' },
  search: { title: 'Search', to: '/search', icon: 'search' },
};

/**
 * Zero-config standalone items configuration
 */
export const DEFAULT_STANDALONE_CONFIG: StandaloneItem[] = [
  { id: 'docs', priority: 100, enabled: true },
  { id: 'settings', priority: 200, enabled: true },
  { id: 'admin', priority: 300, enabled: true },
];

/**
 * Zero-config utility items configuration
 */
export const DEFAULT_UTILITY_CONFIG: UtilityItem[] = [
  { id: 'create', priority: 100, enabled: true },
  { id: 'search', priority: 200, enabled: true },
];

/**
 * Fallback configuration when nothing else is available
 * This should only be used as a last resort
 */
export const FALLBACK_CONFIG: NavigationConfig = {
  groups: [],
  settings: {
    showOther: true,
  },
};

interface GroupDefinition {
  id: string;
  title: string;
  priority: number;
  icon: string;
  matchers: Array<{ kind: string; type?: string }>;
}

export class NavigationDefaults {
  private static readonly GROUP_DEFINITIONS: GroupDefinition[] = [
    {
      id: "all-entities",
      title: "All Entities",
      priority: 10,
      icon: "folder",
      matchers: [
        { kind: "System" },
        { kind: "Domain" },
        { kind: "Component" },
        { kind: "API" },
        { kind: "Resource" },
        { kind: "Template" },
        { kind: "Location" },
        { kind: "Group" },
        { kind: "User" },
      ],
    },
  ];

  static generateDefaultGroups(
    schema: CatalogSchema,
    showUncategorized: boolean = true,
  ): NavigationGroup[] {
    logger.log("Generating default navigation groups from catalog schema");
    logger.log("Settings - showUncategorized:", showUncategorized);
    logger.time("generate-defaults");

    const normalizedSchema = normalizeCatalogSchema(schema);

    const groups: NavigationGroup[] = [];
    const processedEntities = new Set<string>();

    this.GROUP_DEFINITIONS.forEach((groupDef) => {
      const items: NavigationItem[] = [];

      groupDef.matchers.forEach((matcher) => {
        const key = matcher.type
          ? `${matcher.kind}:${matcher.type}`
          : `kind:${matcher.kind}`;

        if (
          this.matchExists(normalizedSchema, matcher) &&
          !processedEntities.has(key)
        ) {
          const item: NavigationItem = {
            title: formatTitle(matcher),
            filter: matcher as CatalogFilter,
            icon: getIconForEntity(matcher.kind, matcher.type),
            dynamic: true,
          };
          items.push(item);
          processedEntities.add(key);

          // If we're adding a kind without type, mark all types for that kind as processed too
          // This prevents them from showing up in "Other"
          if (!matcher.type && normalizedSchema.typesByKind.has(matcher.kind)) {
            const types = normalizedSchema.typesByKind.get(matcher.kind);
            types?.forEach((type) => {
              processedEntities.add(`${matcher.kind}:${type}`);
            });
          }
        }
      });

      if (items.length > 0) {
        groups.push({
          id: groupDef.id,
          title: groupDef.title,
          icon: groupDef.icon,
          priority: groupDef.priority,
          items,
          allowDynamic: true,
        });
      }
    });

    if (showUncategorized) {
      const otherItems = this.findUncategorized(
        normalizedSchema,
        processedEntities,
      );
      if (otherItems.length > 0) {
        groups.push({
          id: "other",
          title: "Other",
          icon: "folder",
          priority: 100,
          items: otherItems,
          allowDynamic: true,
        });
      }
    } else {
      logger.log("Skipping uncategorized items (showUncategorized=false)");
    }

    logger.timeEnd("generate-defaults");
    logger.log(
      `Generated ${groups.length} navigation groups with ${
        groups.reduce(
          (acc, g) => acc + g.items.length,
          0,
        )
      } total items`,
    );

    return groups;
  }

  private static matchExists(
    schema: CatalogSchema,
    matcher: { kind: string; type?: string },
  ): boolean {
    if (!schema.kinds.includes(matcher.kind)) {
      return false;
    }

    if (matcher.type) {
      const types = schema.typesByKind.get(matcher.kind);
      return types?.has(matcher.type) || false;
    }

    return true;
  }

  private static findUncategorized(
    schema: CatalogSchema,
    processed: Set<string>,
  ): NavigationItem[] {
    const items: NavigationItem[] = [];

    schema.kinds.forEach((kind) => {
      const key = `kind:${kind}`;
      if (!processed.has(key)) {
        const internalKinds = ["Location", "User"];
        if (!internalKinds.includes(kind)) {
          items.push({
            title: formatTitle({ kind }),
            filter: { kind } as CatalogFilter,
            icon: getIconForEntity(kind),
            dynamic: true,
          });
          processed.add(key);
        }
      }
    });

    schema.typesByKind.forEach((types, kind) => {
      types.forEach((type) => {
        const key = `${kind}:${type}`;
        if (!processed.has(key)) {
          items.push({
            title: `${humanize(type)} (${kind})`,
            filter: { kind, type } as CatalogFilter,
            icon: getIconForEntity(kind, type),
            dynamic: true,
          });
        }
      });
    });

    logger.log(`Found ${items.length} uncategorized navigation items`);
    return items;
  }
}
